/**
 * Component that shows a helpful message if Firebase is not configured
 */

import { useEffect, useState } from 'react';

export default function FirebaseConfigCheck(): JSX.Element | null {
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    // Check if Firebase config is missing or invalid
    const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
    const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;

    const isMisconfigured =
      !apiKey ||
      !projectId ||
      apiKey.includes('REPLACE') ||
      apiKey.includes('DEMO');

    if (isMisconfigured) {
      setShowWarning(true);
    }
  }, []);

  if (!showWarning) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-8">
        <div className="text-center mb-6">
          <div className="text-6xl mb-4">⚙️</div>
          <h1 className="text-3xl font-bold text-red-600 dark:text-red-400 mb-2">
            Firebase Not Configured
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            The application requires Firebase configuration to run.
          </p>
        </div>

        <div className="bg-gray-100 dark:bg-gray-900 rounded-lg p-6 mb-6 text-left">
          <h2 className="font-bold text-lg mb-3">Quick Fix (5 minutes):</h2>

          <ol className="space-y-3 text-sm">
            <li className="flex items-start">
              <span className="bg-primary-800 text-white rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                1
              </span>
              <div>
                <strong>Get Firebase config:</strong>
                <br />
                Go to{' '}
                <a
                  href="https://console.firebase.google.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-800 dark:text-primary-400 hover:underline"
                >
                  Firebase Console
                </a>{' '}
                → Project Settings → General → Your apps → Web
              </div>
            </li>

            <li className="flex items-start">
              <span className="bg-primary-800 text-white rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                2
              </span>
              <div>
                <strong>Create .env file:</strong>
                <pre className="bg-gray-800 text-green-400 p-2 rounded mt-2 text-xs overflow-x-auto">
                  cp hosting/.env.example hosting/.env
                </pre>
              </div>
            </li>

            <li className="flex items-start">
              <span className="bg-primary-800 text-white rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                3
              </span>
              <div>
                <strong>Edit hosting/.env</strong> with your Firebase config values
              </div>
            </li>

            <li className="flex items-start">
              <span className="bg-primary-800 text-white rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                4
              </span>
              <div>
                <strong>Restart dev server:</strong>
                <pre className="bg-gray-800 text-green-400 p-2 rounded mt-2 text-xs overflow-x-auto">
                  npm run dev
                </pre>
              </div>
            </li>
          </ol>
        </div>

        <div className="text-center">
          <a
            href="https://github.com/yourusername/foxie-systems/blob/main/hosting/FIREBASE_SETUP.md"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary inline-block"
          >
            📖 View Detailed Setup Guide
          </a>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400">
          <strong>Note:</strong> Firebase configuration values are safe to expose in client-side
          code. They are NOT secret credentials.
        </div>
      </div>
    </div>
  );
}
